// src/Routes/allRoutes.tsx
import React from "react";
import { Navigate, LoaderFunctionArgs, useLoaderData } from "react-router-dom";
import Login from "../pages/Authentication/Login";
import ForgetPasswordPage from "../pages/Authentication/ForgetPassword";
import Logout from "../pages/Authentication/Logout";
import Register from "../pages/Authentication/Register";
import HomePage from "../pages/Home/HomePage";
import Contact from "../pages/Home/Contact";
import CreateJobPage from "../pages/Home/CreateJobPage"; // New import
import ListingResults from "../pages/Listing/ListingResults";
import JoyMessagesTemplate from "../Components/messages/App";
import PrivacyPolicy from "pages/Compliance/PrivacyPolicy";
import TermsOfService from "pages/Compliance/TermsOfService";
import ReleaseNotes from "pages/Releases/ReleaseNotes";
import JobSearchPage from "pages/Jobs/JobSearchPage";
import JobCityPage from "pages/Jobs/JobCityPage";
import JobMapPage from "pages/Jobs/JobMapPage";
import JobStateMapPage from "pages/Jobs/JobStateMapPage";


import {
  ContractorsListPage,
  ContractorProfilePage,
  ContractorFormPage,
} from "../pages/Contractor";

// Contractor Listing Page Components
import ContractorListingPage from "../pages/Listing/ContractorListingPage";
import ContractorCategoryList from "../pages/Listing/ContractorCategoryList";
import ContractorLocationList from "../pages/Listing/ContractorLocationList";
import ContractorCategoryPage from "../pages/Listing/ContractorCategoryPage";
import ContractorStatePage from "../pages/Listing/ContractorStatePage";
import ContractorCityPage from "../pages/Listing/ContractorCityPage";
import ContractorCategoryLocationPage from "../pages/Listing/ContractorCategoryLocationPage";

// Additional imports from DevShare
import DashboardProject from "../pages/DashboardProject";

import JobDetailPage from "../pages/DashboardProject/JobDetailPage";
import JobDetailPageWrapper from "../pages/DashboardProject/JobDetailPageWrapper";
import ContractorDashboard from "../pages/DashboardProject/ContractorDashboard";
import AdminDashboard from "../pages/DashboardProject/AdminDashboard";
import TestDashboard from "../pages/DashboardProject/TestDashboard";
import ViewJobNotifications from "../pages/DashboardProject/ViewJobNotifications";
import EcommerceCheckout from "../pages/Ecommerce/EcommerceCheckout";

import ContractDetail from "../pages/Contract/ContractDetail";
import PaymentResult from "../pages/Contract/PaymentResult";

interface RouteConfig {
  path: string;
  component: React.ReactElement;
  loader?: ({ params, request }: LoaderFunctionArgs) => Promise<any>;
  // SEO metadata for route-based optimization
  seo?: {
    titleTemplate?: string;
    descriptionTemplate?: string;
    keywords?: string[];
    schemaType?: string;
  };
}

// Smart wrapper component that renders the correct page based on route type
const SmartContractorRoute: React.FC = () => {
  const loaderData = useLoaderData() as { type: 'state' | 'category'; state?: string; category?: string };

  if (loaderData.type === 'state') {
    return <ContractorStatePage />;
  } else {
    return <ContractorCategoryPage />;
  }
};

const authProtectedRoutes: RouteConfig[] = [
  //This Contractors List is an admin page for looking up contractors
  { path: "/contractors-list", component: <ContractorsListPage /> },
  { path: "/contractor/add", component: <ContractorFormPage /> },
  { path: "/contractor/edit/:contractorId", component: <ContractorFormPage /> },

  // New routes from DevShare
  { path: "/dashboard", component: <DashboardProject /> },
  { path: "/payment-result", component: <PaymentResult /> },
  { path: "/index", component: <DashboardProject /> },
  { path: "/dashboard-contractor", component: <ContractorDashboard /> },
  { path: "/dashboard-test", component: <TestDashboard /> },
  { path: "/dashboard-admin", component: <AdminDashboard /> },
  { path: "/make-payment", component: <EcommerceCheckout /> },
  { path: "/contractors", component: <ContractorsListPage /> },
  {
    path: "/contractor-profile/:contractorId",
    component: <ContractorProfilePage />,
    seo: {
      titleTemplate: "{businessName} - Professional Contractor Profile",
      descriptionTemplate: "View {businessName} contractor profile, reviews, contact info, and request quotes for your project.",
      schemaType: "LocalBusiness"
    }
  },
  {
    path: "/job-detail/:id",
    component: <JobDetailPage />,
    loader: async ({ params }: LoaderFunctionArgs) => {
      return {
        jobId: params.id,
        seoUrl: null,
        contractorId: null
      };
    }
  },
  {
    path: '/jobs/*',
    component: <JobDetailPage />,
    loader: async ({ params, request }: LoaderFunctionArgs) => {
      console.log('Full params:', params);
      const url = new URL(request.url);
      const contractorId = url.searchParams.get('cid');
      const jobId = url.searchParams.get('id');
      const seoUrl = params['*'];

      return {
        jobId,
        seoUrl,
        contractorId
      };
    }
  },
  { path: "/contract/:contractId", component: <ContractDetail /> },

  { path: "*", component: <Navigate to="/dashboard" /> },
];

const publicRoutes: RouteConfig[] = [
  { path: "/", component: <HomePage /> },
  { path: "/contact", component: <Contact /> },
  { path: "/create-job", component: <CreateJobPage /> }, // New public route
  { path: "/search", component: <ListingResults /> },
  { path: "/logout", component: <Logout /> },
  { path: "/login", component: <Login /> },
  { path: "/forgot-password", component: <ForgetPasswordPage /> },

  { path: "/register", component: <Register /> },
  { path: "/messages", component: <JoyMessagesTemplate /> },
  { path: "/privacy-policy", component: <PrivacyPolicy /> },
  { path: "/terms-of-service", component: <TermsOfService /> },

  { path: "/releases", component: <ReleaseNotes /> },

  // New route from DevShare
  // { path: "/notif", component: <ViewJobNotifications /> }, 

  //This Contractors List is a public listing page for browsing contractors
  {
    path: "/contractor-profile/:contractorId",
    component: <ContractorProfilePage />,
    seo: {
      titleTemplate: "{businessName} - Professional Contractor",
      descriptionTemplate: "Contact {businessName} for professional contractor services. Read reviews, view portfolio, and get competitive quotes.",
      keywords: ["contractor", "professional services", "quotes", "reviews"],
      schemaType: "LocalBusiness"
    }
  },

  // Contractor directory routes - SEO friendly pattern
  {
    path: "/contractors",
    component: <ContractorListingPage />,
    loader: async () => ({ view: "main" }),
    seo: {
      titleTemplate: "Professional Contractor Directory | Find Local Contractors",
      descriptionTemplate: "Find trusted local contractors on BidBro. Browse by service category and location, read reviews, and get competitive bids from licensed professionals.",
      keywords: ["contractors", "professional services", "local contractors", "home improvement"],
      schemaType: "ItemList"
    }
  },
  {
    path: "/contractors/categories",
    component: <ContractorCategoryList />,
    seo: {
      titleTemplate: "Contractor Categories Directory | All Service Types",
      descriptionTemplate: "Browse all contractor service categories. Find specialists in construction, electrical, plumbing, HVAC, landscaping, and more professional services.",
      keywords: ["contractor categories", "service types", "construction", "electrical", "plumbing", "HVAC"],
      schemaType: "ItemList"
    }
  },
  // SEO REDIRECT: Handle old category structure
  {
    path: "/contractors/category",
    component: <Navigate to="/contractors/categories" replace />
  },
  {
    path: "/contractors/locations",
    component: <ContractorLocationList />,
    seo: {
      titleTemplate: "Contractor Locations Directory | Find Local Professionals",
      descriptionTemplate: "Find contractors by location. Browse professionals serving cities and states across the country for your project needs.",
      keywords: ["contractor locations", "local contractors", "city contractors", "state contractors"],
      schemaType: "ItemList"
    }
  },

  // Smart route that handles both states and categories
  {
    path: "/contractors/:segment",
    component: <SmartContractorRoute />,
    loader: async ({ params }: LoaderFunctionArgs) => {
      const segment = params.segment?.toLowerCase();
      const validStateCodes = ['al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy'];

      // Check if it's a valid state code (2 letters)
      if (segment && segment.length === 2 && validStateCodes.includes(segment)) {
        return {
          type: 'state',
          state: params.segment
        };
      } else {
        return {
          type: 'category',
          category: params.segment
        };
      }
    },
    seo: {
      titleTemplate: "Contractors Directory",
      descriptionTemplate: "Find trusted contractors. Browse reviews, contact info, and get competitive bids from licensed professionals.",
      keywords: ["contractors", "professional services"],
      schemaType: "ItemList"
    }
  },
  {
    path: "/contractors/:state/:city",
    component: <ContractorCityPage />,
    loader: async ({ params }: LoaderFunctionArgs) => {
      const state = params.state?.toLowerCase();
      const validStateCodes = ['al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy'];

      if (!state || state.length !== 2 || !validStateCodes.includes(state)) {
        throw new Response("Not Found", { status: 404 });
      }

      return {
        state: params.state,
        city: params.city,
      };
    },
    seo: {
      titleTemplate: "Contractors in {city}, {state} | Local Professionals",
      descriptionTemplate: "Find qualified contractors in {city}, {state}. Browse our directory of professional contractors serving {city} for your project needs.",
      keywords: ["{city} contractors", "{state} contractors", "local professionals", "{city}, {state}"],
      schemaType: "ItemList"
    }
  },
  {
    path: "/contractors/:state/:city/:categorySlug",
    component: <ContractorCategoryLocationPage />,
    loader: async ({ params }: LoaderFunctionArgs) => ({
      state: params.state,
      city: params.city,
      category: params.categorySlug,
    }),
    seo: {
      titleTemplate: "{category} in {city}, {state} | Local {category} Contractors",
      descriptionTemplate: "Find professional {category} contractors in {city}, {state}. Browse reviews, contact info, and get competitive bids from licensed {category} professionals.",
      keywords: ["{category} {city}", "{category} {state}", "local {category}", "{city} {category} contractors"],
      schemaType: "ItemList"
    }
  },

  // Individual contractor page
  {
    path: "/contractors/:state/:city/:categorySlug/:contractorId/:businessNameSlug?",
    component: <ContractorProfilePage />,
    loader: async ({ params }: LoaderFunctionArgs) => ({
      state: params.state,
      city: params.city,
      category: params.categorySlug,
      contractorId: params.contractorId,
      businessNameSlug: params.businessNameSlug,
    }),
    seo: {
      titleTemplate: "{businessName} - {category} Contractor in {city}, {state}",
      descriptionTemplate: "Contact {businessName} for professional {category} services in {city}, {state}. Read reviews, view portfolio, and request quotes for your project.",
      keywords: ["{businessName}", "{category} contractor", "{city} {category}", "{state} contractor"],
      schemaType: "LocalBusiness"
    }
  },

  {
    path: "/jobs",
    component: <JobMapPage />,
    seo: {
      titleTemplate: "Job Opportunities Map | Interactive Job Search",
      descriptionTemplate: "Explore job opportunities across the United States with our interactive map. Click on any state to discover local employment and project opportunities.",
      keywords: ["jobs map", "interactive job search", "employment by location", "job opportunities", "project opportunities"],
      schemaType: "WebPage"
    }
  },

  {
    path: "/jobs/:state",
    component: <JobStateMapPage />,
    loader: async ({ params }: LoaderFunctionArgs) => {
      const state = params.state?.toLowerCase();
      const validStateCodes = ['al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy'];

      if (!state || state.length !== 2 || !validStateCodes.includes(state)) {
        throw new Response("Not Found", { status: 404 });
      }

      return {
        state: params.state
      };
    },
    seo: {
      titleTemplate: "Jobs in {state} | {state} Employment Opportunities Map",
      descriptionTemplate: "Interactive map showing job opportunities in {state}. Explore counties and cities with available employment and project opportunities.",
      keywords: ["{state} jobs", "{state} employment", "local job opportunities", "regional employment"],
      schemaType: "WebPage"
    }
  },
  {
    path: "/jobs/:state/:city/:categorySlug",
    component: <JobCityPage />, // Use the same component
    loader: async ({ params }: LoaderFunctionArgs) => {
      const state = params.state?.toLowerCase();
      const validStateCodes = ['al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy'];

      if (!state || state.length !== 2 || !validStateCodes.includes(state)) {
        throw new Response("Not Found", { status: 404 });
      }

      return {
        state: params.state,
        city: params.city,
        category: params.categorySlug, // NEW: Pass the category
      };
    },
    seo: {
      titleTemplate: "{category} Jobs in {city}, {state} | Local {category} Opportunities",
      descriptionTemplate: "Find {category} job opportunities in {city}, {state}. Browse available {category} projects from local homeowners and businesses.",
      keywords: ["{category} jobs", "{city} {category}", "{state} opportunities", "local {category} projects"],
      schemaType: "ItemList"
    }
  },
  {
    path: "/jobs/:state/:city",
    component: <JobCityPage />, // Your existing component, now enhanced
    loader: async ({ params }: LoaderFunctionArgs) => {
      const state = params.state?.toLowerCase();
      const validStateCodes = ['al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy'];

      if (!state || state.length !== 2 || !validStateCodes.includes(state)) {
        throw new Response("Not Found", { status: 404 });
      }

      return {
        state: params.state,
        city: params.city,
      };
    },
    seo: {
      titleTemplate: "Jobs in {city}, {state} | Local Project Opportunities",
      descriptionTemplate: "Find job opportunities and projects in {city}, {state}. Browse our directory of available work for contractors and service professionals in {city}.",
      keywords: ["{city} jobs", "{state} opportunities", "local projects", "{city}, {state}"],
      schemaType: "ItemList"
    }
  },


  { path: "/job/:seo_url/:jobid", component: <JobDetailPageWrapper /> },

  {
    path: "/jobs/search",
    component: <JobSearchPage />, // Keep your existing JobSearchPage for search functionality
    seo: {
      titleTemplate: "Search Jobs | Find Employment Opportunities",
      descriptionTemplate: "Search for job opportunities by keyword, location, and category. Find the perfect employment opportunity or project.",
      keywords: ["job search", "employment search", "find jobs", "career opportunities"],
      schemaType: "WebPage"
    }
  }
];

// SEO utilities for route-based optimization
export const getRouteMetadata = (pathname: string): RouteConfig['seo'] | null => {
  const allRoutes = [...publicRoutes, ...authProtectedRoutes];

  for (const route of allRoutes) {
    // Convert route path to regex pattern
    const pattern = route.path
      .replace(/:[^/]+/g, '[^/]+') // Replace params with regex
      .replace(/\?/g, '\\?'); // Escape optional parts

    const regex = new RegExp(`^${pattern}$`);

    if (regex.test(pathname)) {
      return route.seo || null;
    }
  }

  return null;
};

// Helper to validate if a string is a valid US state code
export const isValidStateCode = (code: string): boolean => {
  const validStateCodes = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
  return validStateCodes.includes(code.toUpperCase());
};

// Helper to determine if a URL segment should be treated as a state vs category
export const isStateRoute = (segment: string): boolean => {
  return segment.length === 2 && isValidStateCode(segment);
};

// Helper to interpolate route templates with actual values
export const interpolateTemplate = (
  template: string,
  values: { [key: string]: string }
): string => {
  let result = template;

  Object.entries(values).forEach(([key, value]) => {
    const cleanValue = value?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
    result = result.replace(new RegExp(`{${key}}`, 'g'), cleanValue);
  });

  return result;
};

export { authProtectedRoutes, publicRoutes };